import { useState, useContext, useEffect, useMemo } from 'react';
import {
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormHelperText,
	Box,
	Button,
	DialogContent,
	DialogTitle,
	Dialog,
	DialogActions,
	Typography,
	Tooltip,
	Checkbox,
	ListItemText,
	Chip,
	Alert,
	IconButton,
	ListSubheader,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	InputAdornment,
	CircularProgress,
} from '@mui/material';
import {
	DeleteOutlineOutlined,
	SearchOutlined,
	KeyboardArrowDown,
	KeyboardArrowRight,
	AddCircleOutline,
	ContentCopyOutlined,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { generateClient } from 'aws-amplify/api';
import { tenantCustomDispos } from 'src/graphql/queries';
import {
	createCustomDispoMapping,
	deleteCustomDispoMapping,
} from 'src/graphql/mutations';
import { fetchAuthSession } from 'aws-amplify/auth';
import { UserContext } from 'src/contexts/UserContext';
import { InContact } from 'src/contexts/InContact';
import { Webex } from 'src/contexts/Webex';
import { PageAppBar } from 'src/components/pageAppBar';
import { ConfirmDialog } from 'src/components/confirmDialog/confirmDialog';
import { actionOneButtonStyle, actionTwoButtonStyle } from 'src/theme';
import { RingCX } from 'src/contexts/RingCX';
import { Genesys } from 'src/contexts/Genesys';
import { fetchAllPages } from 'src/tools/graphqlPaging';

export function DispositionMappings() {
	const client = generateClient();
	const userContext = useContext(UserContext);

	// Skills or queues
	const [skills, setSkills] = useState([]);
	const [selectedSkills, setSelectedSkills] = useState([]); // Multi-select

	const [allDispositions, setAllDispositions] = useState([]); // All dispositions fetched
	const [filteredDispositions, setFilteredDispositions] = useState([]); // Dispositions not already mapped

	const [mappings, setMappings] = useState([]);
	const [selectedMapping, setSelectedMapping] = useState(); // For deletion

	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [loading, setLoading] = useState(false);
	const [dispositionsLoading, setDispositionsLoading] = useState(false);

	const [showDispositionMappingDialog, setShowDispositionMappingDialog] =
		useState(false);

	const [dispoOutcomes, setDispoOutcomes] = useState([]);

	// Clone dialog state
	const [showCloneDialog, setShowCloneDialog] = useState(false);
	const [cloneSourceGroup, setCloneSourceGroup] = useState(null);
	const [cloneTargetSkill, setCloneTargetSkill] = useState('');
	const [cloning, setCloning] = useState(false);

	// Table state
	const [searchTerm, setSearchTerm] = useState('');
	const [expandedSkills, setExpandedSkills] = useState(null); // null = all expanded by default
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10); // Skills per page

	const initialMappingRow = { customDispo: '', outcome: '' };
	const [mappingRows, setMappingRows] = useState([{ ...initialMappingRow }]);

	const outcomes = [
		{ dispClassId: 1, dispCode: 10, name: 'Positive w/Amount', description: 'Agent entered a positive amount' },
		{ dispClassId: 2, dispCode: 10, name: 'Positive no Amount', description: 'Agent does not enter a positive amount' },
		{ dispClassId: 3, dispCode: 99, name: 'Negative — DNC — Skill', description: 'Contact request to be added to skills DNC' },
		{ dispClassId: 4, dispCode: 98, name: 'Negative — DNC — BU', description: 'Contact requests to be added to BU DNC' },
		{ dispClassId: 5, dispCode: 10, name: 'Negative', description: 'Agent received a negative response' },
		{ dispClassId: 6, dispCode: 10, name: 'Other', description: 'No further effort required- Bad Number responses.' },
		{ dispClassId: 12, dispCode: 0, name: 'Final — Answering Machine', description: 'Answering machine reached - message left' },
		{ dispClassId: 16, dispCode: 6, name: 'Error', description: 'Can not reach number, Network Error' },
		{ dispClassId: 18, dispCode: 20, name: 'Invalid Number', description: 'The number the dialer called was invalid' },
		{ dispClassId: 21, dispCode: 22, name: 'Number Changed', description: 'The called party number is not longer assigned' },
		{ dispClassId: 22, dispCode: 21, name: 'Disconnect', description: 'The called party has a disconnected number' },
		{ dispClassId: 13, dispCode: 3, name: 'Fax Machine', description: 'Agent Determined Number is for a fax machine' },
		{ dispClassId: 14, dispCode: 33, name: 'Answering Machine Left Message', description: 'Reached Answering Machine - Left Message' },
		{ dispClassId: 11, dispCode: 5, name: 'Retry - Answering Machine', description: 'Retry record later' },
		{ dispClassId: 10, dispCode: 89, name: 'Retry - Not Available', description: 'The requested person was unavailable.' },
		{ dispClassId: 32, dispCode: 31, name: 'Called Party Hang Up', description: 'The contact hung up before being connected to an agent.' },
	];

	document.title = 'Disposition Mappings';

	/**
	 * Gets the skills and mappings on mount — in parallel for faster load.
	 */
	useEffect(() => {
		async function getData() {
			let availableSkills = [];
			const bearerToken = (await fetchAuthSession()).tokens.idToken;
			let finalOutcomes = [...outcomes];
			try {
				// Start mappings load immediately (doesn't depend on skills)
				const mappingsPromise = loadMappingsRaw();

				switch (userContext.telephonyProvider) {
					case 'NiC':
						availableSkills = await InContact.getActivePhoneSkills();
						finalOutcomes.push({
							dispClassId: 8, dispCode: 8,
							name: 'Scheduled Callback',
							description: 'The contact requested a callback.',
						});
						break;
					case 'Webex':
						finalOutcomes.push({
							dispClassId: 9, dispCode: 9,
							name: 'Scheduled Callback',
							description: 'The contact requested a callback.',
						});
						const webexQueues = await Webex.getEntryPoints(
							bearerToken, userContext.apiKey,
						);
						availableSkills = webexQueues.map((queue) => ({
							skillId: queue.id, skillName: queue.name,
						}));
						break;
					case 'RingCX':
						const ringCXDialGroups = await RingCX.getRingCampaigns(
							bearerToken, userContext.apiKey,
						);
						const ringCXTranslatedGroups = [];
						for (const dialGroup of ringCXDialGroups) {
							for (const campaign of dialGroup.campaigns) {
								ringCXTranslatedGroups.push({
									skillId: campaign.campaignId,
									skillName: campaign.campaignName,
									dialGroup: campaign.dialGroup.id,
								});
							}
						}
						availableSkills = ringCXTranslatedGroups;
						break;
					case 'Genesys':
						const genesysQueues = await Genesys.getQueues(
							bearerToken, userContext.apiKey,
						);
						availableSkills = genesysQueues.map((queue) => ({
							skillId: queue.id, skillName: queue.name,
						}));
						break;
					default:
						console.error(`Unsupported telephony provider: ${userContext.telephonyProvider}`);
						break;
				}

				const sortedSkills = availableSkills.concat().sort(sortBy('skillName'));
				setSkills(sortedSkills);

				// Wait for mappings and resolve skill names
				const rawMappings = await mappingsPromise;
				const skillMap = new Map(sortedSkills.map((s) => [String(s.skillId), s.skillName]));
				for (const mapping of rawMappings) {
					mapping.skillName = skillMap.get(String(mapping.skill)) ?? 'Deleted Skill';
				}
				setMappings(rawMappings);
			} catch (error) {
				console.error(error);
				enqueueSnackbar('Error loading data.', { variant: 'error', autoHideDuration: 5000 });
			}
			setLoading(false);
			setDispoOutcomes(finalOutcomes);
		}

		if (userContext.tenantId && userContext.telephonyProvider) {
			setLoading(true);
			getData();
		}
	}, [userContext.tenantId, userContext.telephonyProvider]);

	/**
	 * Fetch dispositions when selected skills change.
	 * For NiC/RingCX/Genesys, uses first selected skill to get the disposition list.
	 * For Webex, dispositions are global (not skill-specific).
	 */
	useEffect(() => {
		if (selectedSkills.length > 0) {
			fetchDispositions(selectedSkills);
		} else {
			setAllDispositions([]);
			setFilteredDispositions([]);
		}
	}, [selectedSkills]);

	/**
	 * Re-filter dispositions when mappings change.
	 */
	useEffect(() => {
		if (allDispositions.length > 0 && selectedSkills.length > 0) {
			filterAlreadyMappedDispositions(allDispositions, selectedSkills);
		}
	}, [mappings, allDispositions]);

	/**
	 * Fetches raw mappings from GraphQL (no skill name resolution).
	 * Used during initial parallel load.
	 */
	async function loadMappingsRaw() {
		return fetchAllPages({
			client,
			query: tenantCustomDispos,
			resultKey: 'tenantCustomDispos',
			variables: { tenant: userContext.tenantId },
		});
	}

	/**
	 * Reloads mappings after a create/delete and resolves skill names.
	 */
	async function reloadMappings() {
		setLoading(true);
		try {
			const rawMappings = await loadMappingsRaw();
			const skillMap = new Map(skills.map((s) => [String(s.skillId), s.skillName]));
			for (const mapping of rawMappings) {
				mapping.skillName = skillMap.get(String(mapping.skill)) ?? 'Deleted Skill';
			}
			setMappings(rawMappings);
		} catch (error) {
			console.log('Error loading disposition mappings: ', error);
			enqueueSnackbar('Error loading disposition mappings.', { variant: 'error', autoHideDuration: 5000 });
		}
		setLoading(false);
	}

	/**
	 * Runs promises in batches to limit concurrency.
	 */
	async function batchedPromises(items, fn, batchSize = 5) {
		const results = [];
		for (let i = 0; i < items.length; i += batchSize) {
			const batch = items.slice(i, i + batchSize);
			const batchResults = await Promise.all(batch.map(fn));
			results.push(...batchResults);
		}
		return results;
	}

	/**
	 * Fetches dispositions from the telephony provider for ALL selected skills
	 * and builds a deduplicated union set.
	 * Batches requests (5 at a time) to avoid overwhelming the API.
	 */
	async function fetchDispositions(skillIds) {
		setDispositionsLoading(true);
		try {
			const token = (await fetchAuthSession()).tokens.idToken;
			let dispositions = [];

			switch (userContext.telephonyProvider) {
				case 'NiC': {
					const allResults = await batchedPromises(
						skillIds,
						(skillId) => InContact.getCustomDispositions(skillId),
					);
					const seen = new Set();
					for (const result of allResults) {
						for (const dispo of (result ?? [])) {
							const id = dispo.dispositionId ?? dispo.dispositionCode ?? dispo.id;
							if (!seen.has(id)) {
								seen.add(id);
								dispositions.push(dispo);
							}
						}
					}
					break;
				}
				case 'Webex': {
					const response = await Webex.getWrapUpCodes(token, userContext.apiKey);
					dispositions = await response.body.json();
					break;
				}
				case 'RingCX': {
					const allResults = await batchedPromises(
						skillIds,
						(skillId) => {
							const dialGroupId = skills.find(
								(skill) => skill.skillId == skillId,
							)?.dialGroup;
							return RingCX.getCampaignDispositions(
								token, userContext.apiKey, skillId, dialGroupId,
							);
						},
					);
					const seen = new Set();
					for (const result of allResults) {
						for (const dispo of (result ?? [])) {
							const id = dispo.disposition;
							if (!seen.has(id)) {
								seen.add(id);
								dispositions.push(dispo);
							}
						}
					}
					break;
				}
				case 'Genesys': {
					const allResults = await batchedPromises(
						skillIds,
						(skillId) => Genesys.getWrapUps(token, userContext.apiKey, skillId),
					);
					const seen = new Set();
					for (const result of allResults) {
						for (const dispo of (result?.entities ?? [])) {
							const id = dispo.id;
							if (!seen.has(id)) {
								seen.add(id);
								dispositions.push(dispo);
							}
						}
					}
					break;
				}
				default:
					break;
			}
			setAllDispositions(dispositions);
			filterAlreadyMappedDispositions(dispositions, skillIds);
		} catch (error) {
			console.error('Error fetching dispositions:', error);
			enqueueSnackbar('Error loading dispositions.', { variant: 'error', autoHideDuration: 5000 });
		}
		setDispositionsLoading(false);
	}

	/**
	 * Filters out dispositions that are already mapped for ALL selected skills.
	 */
	function filterAlreadyMappedDispositions(dispositions, skillIds) {
		const filtered = dispositions.filter((disposition) => {
			const dispoId = disposition.dispositionId ?? disposition.dispositionCode ?? disposition.id;
			// Only filter out if it's mapped to ALL selected skills
			const mappedToAll = skillIds.every((skillId) =>
				mappings.find((m) => m.custDispId === dispoId && m.skill == skillId),
			);
			return !mappedToAll;
		});
		setFilteredDispositions(filtered);
	}

	/**
	 * Handles multi-skill selection change.
	 */
	function handleSkillChange(event) {
		const value = event.target.value;
		// MUI Select with multiple returns an array
		setSelectedSkills(typeof value === 'string' ? value.split(',') : value);
		setPage(0);
	}

	/**
	 * Deletes a disposition mapping.
	 */
	async function deleteDispositionMapping(mappingId) {
		try {
			await client.graphql({
				query: deleteCustomDispoMapping,
				variables: { input: { id: mappingId } },
			});
			enqueueSnackbar('Mapping deleted.', { variant: 'success', autoHideDuration: 5000 });
			await reloadMappings();
		} catch (error) {
			console.error('Error deleting mapping: ', error);
			enqueueSnackbar('Error deleting mapping.', { variant: 'error', autoHideDuration: 5000 });
		}
	}

	/**
	 * Creates mappings for ALL rows × ALL selected skills at once.
	 */
	async function handleSubmit() {
		try {
			// Validate rows
			const validRows = mappingRows.filter((row) => row.customDispo && row.outcome);
			if (validRows.length === 0) {
				enqueueSnackbar('Please complete at least one mapping row.', { variant: 'warning' });
				return;
			}

			const createInputs = [];

			for (const row of validRows) {
				const outcomeInfo = dispoOutcomes.find(
					(outcome) => outcome.dispClassId === Number(row.outcome),
				);

				const dispoId = userContext.telephonyProvider !== 'RingCX'
					? (row.customDispo.dispositionId ?? row.customDispo.dispositionCode ?? row.customDispo.id)
					: row.customDispo.disposition;
				const dispoName = getDispositionName(row.customDispo);

				for (const skillId of selectedSkills) {
					// Check if already mapped for this skill
					const alreadyMapped = mappings.find(
						(m) => m.custDispId === dispoId && m.skill == skillId,
					);
					if (alreadyMapped) continue;

					createInputs.push({
						tenant: userContext.tenantId,
						custDispId: dispoId,
						custDispName: dispoName,
						dispClassId: outcomeInfo.dispClassId,
						dispCode: outcomeInfo.dispCode,
						dispDescription: outcomeInfo.description,
						dispClassName: outcomeInfo.name,
						skill: skillId,
					});
				}
			}

			if (createInputs.length === 0) {
				enqueueSnackbar('All selected mappings already exist.', { variant: 'info' });
				setShowDispositionMappingDialog(false);
				return;
			}

			// Batch in groups of 5 to avoid overwhelming API
			for (let i = 0; i < createInputs.length; i += 5) {
				const batch = createInputs.slice(i, i + 5);
				await Promise.all(
					batch.map((input) =>
						client.graphql({
							query: createCustomDispoMapping,
							variables: { input },
						}),
					),
				);
			}

			setMappingRows([{ ...initialMappingRow }]);
			setShowDispositionMappingDialog(false);
			await reloadMappings();

			const skillCount = selectedSkills.length;
			enqueueSnackbar(
				`${validRows.length} mapping${validRows.length > 1 ? 's' : ''} created for ${skillCount} ${skillCount > 1 ? (userContext.skillLabel?.toLowerCase() + 's') : userContext.skillLabel?.toLowerCase()}.`,
				{ variant: 'success' },
			);
		} catch (err) {
			console.error(err);
			enqueueSnackbar('Error creating disposition mappings.', { variant: 'error' });
		}
	}

	/**
	 * Updates a specific mapping row field.
	 */
	function updateMappingRow(index, field, value) {
		setMappingRows((prev) => {
			const updated = [...prev];
			updated[index] = { ...updated[index], [field]: value };
			return updated;
		});
	}

	/**
	 * Adds a new empty mapping row.
	 */
	function addMappingRow() {
		setMappingRows((prev) => [...prev, { ...initialMappingRow }]);
	}

	/**
	 * Removes a mapping row by index.
	 */
	function removeMappingRow(index) {
		setMappingRows((prev) => {
			if (prev.length === 1) return [{ ...initialMappingRow }];
			return prev.filter((_, i) => i !== index);
		});
	}

	/**
	 * Clones all mappings from a source skill to a target skill.
	 */
	async function cloneMappings() {
		if (!cloneSourceGroup || !cloneTargetSkill) return;

		setCloning(true);
		try {
			const sourceSkillMappings = cloneSourceGroup.mappings;
			const createInputs = [];

			for (const mapping of sourceSkillMappings) {
				// Skip if already mapped on the target skill
				const alreadyExists = mappings.find(
					(m) => m.custDispId === mapping.custDispId && m.skill == cloneTargetSkill,
				);
				if (alreadyExists) continue;

				createInputs.push({
					tenant: userContext.tenantId,
					custDispId: mapping.custDispId,
					custDispName: mapping.custDispName,
					dispClassId: mapping.dispClassId,
					dispCode: mapping.dispCode,
					dispDescription: mapping.dispDescription,
					dispClassName: mapping.dispClassName,
					skill: cloneTargetSkill,
				});
			}

			if (createInputs.length === 0) {
				enqueueSnackbar('All mappings already exist on the target. Nothing to clone.', { variant: 'info' });
				setShowCloneDialog(false);
				setCloneTargetSkill('');
				setCloning(false);
				return;
			}

			// Batch in groups of 5
			for (let i = 0; i < createInputs.length; i += 5) {
				const batch = createInputs.slice(i, i + 5);
				await Promise.all(
					batch.map((input) =>
						client.graphql({
							query: createCustomDispoMapping,
							variables: { input },
						}),
					),
				);
			}

			const targetName = getSkillName(cloneTargetSkill);
			enqueueSnackbar(
				`${createInputs.length} mapping${createInputs.length !== 1 ? 's' : ''} cloned to ${targetName}.`,
				{ variant: 'success' },
			);
			setShowCloneDialog(false);
			setCloneTargetSkill('');
			await reloadMappings();
		} catch (err) {
			console.error('Error cloning mappings:', err);
			enqueueSnackbar('Error cloning mappings.', { variant: 'error' });
		}
		setCloning(false);
	}

	/**
	 * Gets dispositions available for a row (not already selected in other rows).
	 */
	function getAvailableDispositionsForRow(rowIndex) {
		const selectedInOtherRows = new Set(
			mappingRows
				.filter((_, i) => i !== rowIndex)
				.filter((row) => row.customDispo)
				.map((row) => {
					const dispo = row.customDispo;
					return String(dispo.dispositionId ?? dispo.dispositionCode ?? dispo.id ?? dispo.disposition);
				}),
		);

		return filteredDispositions.filter((disposition) => {
			const dispoId = String(disposition.dispositionId ?? disposition.dispositionCode ?? disposition.id ?? disposition.disposition);
			return !selectedInOtherRows.has(dispoId);
		});
	}

	function getDispositionName(disposition) {
		switch (userContext.telephonyProvider) {
			case 'Webex':
				return disposition.name;
			case 'RingCX':
				return disposition.disposition;
			default:
				return disposition.dispositionName ?? disposition.description ?? disposition.name;
		}
	}

	const sortBy = (key) => {
		return (a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0);
	};

	/**
	 * Group mappings by skill for the friendly grouped display.
	 */
	const groupedMappings = useMemo(() => {
		const groups = {};
		for (const mapping of mappings) {
			const skillName = mapping.skillName || 'Unknown';
			if (!groups[skillName]) {
				groups[skillName] = { skillName, skillId: mapping.skill, mappings: [] };
			}
			groups[skillName].mappings.push(mapping);
		}
		// Sort groups alphabetically
		return Object.values(groups).sort(sortBy('skillName'));
	}, [mappings]);

	/**
	 * Filter grouped mappings by search term.
	 */
	const filteredGroupedMappings = useMemo(() => {
		let groups = groupedMappings;

		// Filter to only selected skills if any are selected
		if (selectedSkills.length > 0) {
			groups = groups.filter((group) =>
				selectedSkills.some((skillId) => group.skillId == skillId),
			);
		}

		// Filter by search term
		if (searchTerm) {
			const term = searchTerm.toLowerCase();
			groups = groups
				.map((group) => ({
					...group,
					mappings: group.mappings.filter(
						(m) =>
							m.custDispName?.toLowerCase().includes(term) ||
							m.dispClassName?.toLowerCase().includes(term) ||
							m.skillName?.toLowerCase().includes(term),
					),
				}))
				.filter((group) => group.mappings.length > 0);
		}

		return groups;
	}, [groupedMappings, searchTerm, selectedSkills]);

	/**
	 * Paginated slice of the filtered groups.
	 */
	const paginatedGroups = useMemo(() => {
		const start = page * rowsPerPage;
		return filteredGroupedMappings.slice(start, start + rowsPerPage);
	}, [filteredGroupedMappings, page, rowsPerPage]);

	/**
	 * Total mapping count across filtered groups (for display).
	 */
	const totalFilteredMappings = useMemo(() => {
		return filteredGroupedMappings.reduce((sum, g) => sum + g.mappings.length, 0);
	}, [filteredGroupedMappings]);

	/**
	 * Toggle expand/collapse for a skill group.
	 */
	function toggleSkillGroup(skillName) {
		setExpandedSkills((prev) => {
			// If null (all expanded by default), initialize with all expanded except the toggled one
			if (prev === null) {
				const state = {};
				groupedMappings.forEach((g) => { state[g.skillName] = true; });
				state[skillName] = false;
				return state;
			}
			return { ...prev, [skillName]: !prev[skillName] };
		});
	}

	/**
	 * Expand all skill groups.
	 */
	function expandAll() {
		setExpandedSkills(null); // null means all expanded
	}

	/**
	 * Collapse all skill groups.
	 */
	function collapseAll() {
		const collapsed = {};
		groupedMappings.forEach((g) => { collapsed[g.skillName] = false; });
		setExpandedSkills(collapsed);
	}

	/**
	 * Get skill name by ID.
	 */
	function getSkillName(skillId) {
		return skills.find((s) => s.skillId == skillId)?.skillName ?? skillId;
	}

	return (
		<div style={{ margin: 'auto', maxWidth: '1800px' }}>
			<PageAppBar
				title="Disposition Mappings"
				description="Configure how your dispositions map to outcomes"
			/>

			{userContext.telephonyProvider && (
				<>
					{/* Multi-skill selection and Add Mapping button */}
					<Box sx={{ mb: 3 }}>
						<FormControl fullWidth sx={{ mb: 2 }}>
							<InputLabel id="skills-select-label" color="primary">
								{userContext.skillLabel}s to Map
							</InputLabel>
							<Select
								multiple
								value={selectedSkills}
								inputProps={{ id: 'skills-select' }}
								label={`${userContext.skillLabel}s to Map`}
								labelId="skills-select-label"
								color="primary"
								onChange={handleSkillChange}
								renderValue={(selected) => (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
										{selected.map((skillId) => (
											<Chip
												key={skillId}
												label={getSkillName(skillId)}
												size="small"
												onDelete={() => {
													setSelectedSkills((prev) => prev.filter((id) => id !== skillId));
												}}
												onMouseDown={(e) => e.stopPropagation()}
											/>
										))}
									</Box>
								)}
							>
								{/* Select All / Clear All actions */}
								<ListSubheader disableSticky sx={{ px: 1, py: 0.5, lineHeight: 'unset' }}>
									<Box sx={{ display: 'flex', gap: 1 }}>
										<Button
											size="small"
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => setSelectedSkills(skills.map((s) => s.skillId))}
											sx={{ textTransform: 'none', fontWeight: 500 }}
										>
											Select All ({skills.length})
										</Button>
										<Button
											size="small"
											color="error"
											disabled={selectedSkills.length === 0}
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => setSelectedSkills([])}
											sx={{ textTransform: 'none', fontWeight: 500 }}
										>
											Clear All
										</Button>
									</Box>
								</ListSubheader>
								<Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 1, my: 0.5 }} />
								{skills.map((skill) => (
									<MenuItem key={skill.skillId} value={skill.skillId}>
										<Checkbox checked={selectedSkills.indexOf(skill.skillId) > -1} />
										<ListItemText primary={skill.skillName} />
									</MenuItem>
								))}
							</Select>
							<FormHelperText>
								Select one or more {userContext.skillLabel?.toLowerCase()}s — the mapping will be applied to all selected
							</FormHelperText>
						</FormControl>

						{selectedSkills.length > 0 && (
							<Button
								size="small"
								color="error"
								onClick={() => setSelectedSkills([])}
								sx={{ mb: 2 }}
							>
								Clear Selection ({selectedSkills.length})
							</Button>
						)}

						{userContext.telephonyProvider !== 'Webex' && selectedSkills.length > 1 && (
							<Alert severity="info" sx={{ mb: 2 }}>
								Dispositions are loaded from all selected {userContext.skillLabel?.toLowerCase()}s and merged into a combined list.
								If a disposition appears in multiple {userContext.skillLabel?.toLowerCase()}s, it will only show once.
							</Alert>
						)}

						<Tooltip
							title={
								selectedSkills.length > 0 && filteredDispositions.length === 0
									? `All dispositions for the selected ${userContext.skillLabel?.toLowerCase()}(s) are already mapped`
									: ''
							}
						>
							<span>
								<Button
									disabled={
										selectedSkills.length === 0 ||
										filteredDispositions.length === 0 ||
										dispositionsLoading ||
										userContext.securityOptions.connection < 2
									}
									onClick={() => setShowDispositionMappingDialog(true)}
									sx={actionOneButtonStyle}
								>
									{dispositionsLoading ? (
										<CircularProgress size={20} sx={{ mr: 1 }} />
									) : null}
									Add Disposition Mappings
								</Button>
							</span>
						</Tooltip>
					</Box>

					{/* Add Mapping Dialog — Multi-row table */}
					<Dialog
						open={showDispositionMappingDialog}
						fullWidth
						maxWidth="md"
						onClose={() => {
							setShowDispositionMappingDialog(false);
							setMappingRows([{ ...initialMappingRow }]);
						}}
					>
						<DialogTitle sx={{ pb: 1 }}>
							Add Disposition Mappings
						</DialogTitle>
						<DialogContent>
							<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
								Mapping to {selectedSkills.length} {userContext.skillLabel?.toLowerCase()}
								{selectedSkills.length > 1 ? 's' : ''}:
							</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
								{selectedSkills.map((skillId) => (
									<Chip
										key={skillId}
										label={getSkillName(skillId)}
										size="small"
										color="primary"
										variant="outlined"
									/>
								))}
							</Box>

							<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
												{userContext.dispositionLabel}
											</TableCell>
											<TableCell sx={{ fontWeight: 600, width: 60, backgroundColor: 'background.paper', textAlign: 'center' }}>
											</TableCell>
											<TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
												Outcome
											</TableCell>
											<TableCell sx={{ width: 50, backgroundColor: 'background.paper' }} />
										</TableRow>
									</TableHead>
									<TableBody>
										{mappingRows.map((row, index) => (
											<TableRow key={index} hover>
												<TableCell sx={{ py: 1.5 }}>
													<FormControl fullWidth size="small">
														<Select
															value={row.customDispo}
															displayEmpty
															onChange={(e) => updateMappingRow(index, 'customDispo', e.target.value)}
														>
															<MenuItem value="" disabled>
																<Typography variant="body2" color="text.secondary">
																	Select {userContext.dispositionLabel?.toLowerCase()}
																</Typography>
															</MenuItem>
															{getAvailableDispositionsForRow(index).map((disposition) => (
																<MenuItem
																	key={disposition.dispositionId ?? disposition.dispositionCode ?? disposition.id}
																	value={disposition}
																>
																	{getDispositionName(disposition)}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</TableCell>
												<TableCell sx={{ py: 1.5, textAlign: 'center' }}>
													<Typography variant="caption" color="text.secondary">
														→
													</Typography>
												</TableCell>
												<TableCell sx={{ py: 1.5 }}>
													<FormControl fullWidth size="small">
														<Select
															value={row.outcome}
															displayEmpty
															onChange={(e) => updateMappingRow(index, 'outcome', e.target.value)}
														>
															<MenuItem value="" disabled>
																<Typography variant="body2" color="text.secondary">
																	Select outcome
																</Typography>
															</MenuItem>
															{dispoOutcomes.map((outcome) => (
																<MenuItem key={outcome.dispClassId} value={outcome.dispClassId}>
																	<Box>
																		<Typography variant="body2">{outcome.name}</Typography>
																		<Typography variant="caption" color="text.secondary">
																			{outcome.description}
																		</Typography>
																	</Box>
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</TableCell>
												<TableCell sx={{ py: 1.5 }}>
													<IconButton
														size="small"
														color="error"
														onClick={() => removeMappingRow(index)}
														disabled={mappingRows.length === 1 && !row.customDispo && !row.outcome}
													>
														<DeleteOutlineOutlined fontSize="small" />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>

							<Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1.5 }}>
								<Button
									size="small"
									startIcon={<AddCircleOutline />}
									onClick={addMappingRow}
									disabled={filteredDispositions.length <= mappingRows.length}
								>
									Add Row
								</Button>
							</Box>
						</DialogContent>
						<DialogActions sx={{ px: 3, pb: 2 }}>
							<Button
								onClick={() => {
									setShowDispositionMappingDialog(false);
									setMappingRows([{ ...initialMappingRow }]);
								}}
								sx={actionTwoButtonStyle}
							>
								Cancel
							</Button>
							<Button
								onClick={handleSubmit}
								sx={actionOneButtonStyle}
								disabled={!mappingRows.some((row) => row.customDispo && row.outcome)}
							>
								Save {mappingRows.filter((r) => r.customDispo && r.outcome).length || ''} Mapping{mappingRows.filter((r) => r.customDispo && r.outcome).length !== 1 ? 's' : ''}
							</Button>
						</DialogActions>
					</Dialog>

					{/* Grouped Mappings Display */}
					<Paper variant="outlined" sx={{ mt: 3 }}>
						{/* Search and Controls */}
						<Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
							<Typography variant="h6">
								{selectedSkills.length > 0 ? 'Filtered Mappings' : 'All Mappings'}
								{mappings.length > 0 && (
									<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
										({totalFilteredMappings} mapping{totalFilteredMappings !== 1 ? 's' : ''} across {filteredGroupedMappings.length} {userContext.skillLabel?.toLowerCase()}
										{filteredGroupedMappings.length !== 1 ? 's' : ''}
										{selectedSkills.length > 0 ? ` · ${mappings.length} total` : ''})
									</Typography>
								)}
							</Typography>
							<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
								<Button size="small" onClick={expandAll} disabled={groupedMappings.length === 0}>
									Expand All
								</Button>
								<Button size="small" onClick={collapseAll} disabled={groupedMappings.length === 0}>
									Collapse All
								</Button>
							</Box>
						</Box>

						<Box sx={{ px: 2, pb: 2 }}>
							<TextField
								size="small"
								placeholder="Search mappings..."
								value={searchTerm}
								onChange={(e) => {
									setSearchTerm(e.target.value);
									setPage(0);
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchOutlined fontSize="small" />
										</InputAdornment>
									),
								}}
								sx={{ minWidth: 300 }}
							/>
						</Box>

						{loading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
								<CircularProgress />
							</Box>
						) : filteredGroupedMappings.length === 0 ? (
							<Box sx={{ p: 4, textAlign: 'center' }}>
								<Typography color="text.secondary">
									{searchTerm
										? 'No mappings match your search'
										: 'No disposition mappings configured yet'}
								</Typography>
							</Box>
						) : (
							<>
								<TableContainer>
									<Table size="small">
										{paginatedGroups.map((group) => {
											const isExpanded = expandedSkills === null || expandedSkills[group.skillName] === true;
											return (
												<TableBody key={group.skillName}>
													{/* Group Header Row */}
													<TableRow
														hover
														onClick={() => toggleSkillGroup(group.skillName)}
														sx={{ cursor: 'pointer', backgroundColor: 'action.hover' }}
													>
														<TableCell sx={{ width: 40, p: 1 }}>
															<IconButton size="small">
																{isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
															</IconButton>
														</TableCell>
														<TableCell colSpan={3} sx={{ fontWeight: 600 }}>
															<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
																{group.skillName}
																<Chip
																	label={`${group.mappings.length} mapping${group.mappings.length !== 1 ? 's' : ''}`}
																	size="small"
																	variant="outlined"
																	color="primary"
																/>
																<Tooltip title={`Clone mappings to another ${userContext.skillLabel?.toLowerCase()}`}>
																	<IconButton
																		size="small"
																		color="primary"
																		disabled={userContext.securityOptions.connection < 2}
																		onClick={(e) => {
																			e.stopPropagation();
																			setCloneSourceGroup(group);
																			setCloneTargetSkill('');
																			setShowCloneDialog(true);
																		}}
																	>
																		<ContentCopyOutlined fontSize="small" />
																	</IconButton>
																</Tooltip>
															</Box>
														</TableCell>
													</TableRow>

													{/* Column Headers (only when expanded) */}
													{isExpanded && (
														<TableRow>
															<TableCell sx={{ width: 40 }} />
															<TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>
																{userContext.dispositionLabel}
															</TableCell>
															<TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>
																Outcome
															</TableCell>
															<TableCell sx={{ width: 60, fontWeight: 500, color: 'text.secondary' }} align="right">
																Actions
															</TableCell>
														</TableRow>
													)}

													{/* Mapping Rows */}
													{isExpanded && group.mappings.map((mapping) => (
														<TableRow key={mapping.id} hover>
															<TableCell sx={{ width: 40 }} />
															<TableCell>{mapping.custDispName}</TableCell>
															<TableCell>
																<Chip
																	label={mapping.dispClassName}
																	size="small"
																	color={getOutcomeColor(mapping.dispClassName)}
																	variant="outlined"
																/>
															</TableCell>
															<TableCell align="right">
																<Tooltip title="Delete Mapping">
																	<span>
																		<IconButton
																			size="small"
																			color="error"
																			disabled={userContext.securityOptions.connection < 2}
																			onClick={() => {
																				setSelectedMapping(mapping);
																				setShowDeleteConfirmation(true);
																			}}
																		>
																			<DeleteOutlineOutlined fontSize="small" />
																		</IconButton>
																	</span>
																</Tooltip>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											);
										})}
									</Table>
								</TableContainer>
								<TablePagination
									component="div"
									count={filteredGroupedMappings.length}
									page={page}
									onPageChange={(_, newPage) => setPage(newPage)}
									rowsPerPage={rowsPerPage}
									onRowsPerPageChange={(e) => {
										setRowsPerPage(parseInt(e.target.value, 10));
										setPage(0);
									}}
									rowsPerPageOptions={[5, 10, 25, 50]}
									labelRowsPerPage={`${userContext.skillLabel}s per page:`}
								/>
							</>
						)}
					</Paper>
				</>
			)}

			{/* Delete Confirmation */}
			<ConfirmDialog
				open={showDeleteConfirmation}
				title="Delete Disposition Mapping"
				description="Are you sure you want to delete this disposition mapping? This action cannot be undone."
				actionOneText="Delete"
				actionOneHandler={() => {
					deleteDispositionMapping(selectedMapping.id);
					setShowDeleteConfirmation(false);
				}}
				actionTwoText="Cancel"
				actionTwoHandler={() => {
					setShowDeleteConfirmation(false);
				}}
			/>
		</div>
	);
}

/**
 * Returns a color for the outcome chip based on the outcome name.
 */
function getOutcomeColor(outcomeName) {
	if (!outcomeName) return 'default';
	const name = outcomeName.toLowerCase();
	if (name.includes('positive')) return 'success';
	if (name.includes('negative') || name.includes('dnc')) return 'error';
	if (name.includes('retry') || name.includes('callback')) return 'warning';
	if (name.includes('error') || name.includes('disconnect') || name.includes('invalid')) return 'error';
	return 'default';
}
